import PortalDocument, {PortalDocumentInterface} from "../../document/PortalDocument";
import {TextSearchGlobals} from "../../document/components/pdf/TextSearch";
import React, {ReactNode} from "react";
import {Document, Page, pdfjs} from "react-pdf";
import {ViewerMode} from "../ViewerMode";
import portalConfiguration from "../../../shared/configuration/PortalConfiguration";
import {Attachment} from "@softvision/webpdf-wsclient-9-typescript/lib/generated-sources";
import PortalContext from "../../PortalContext";
import Immutable from "../../../shared/immutable/Immutable";
import FunctionHelper from "../../../shared/helper/FunctionHelper";
import UnitHelper from "../../../shared/helper/UnitHelper";
import LengthUnit from "../../../shared/units/LengthUnit";
import {DebugGlobals} from "../../dialog/components/settings/DebugDialog";
import PortalPureComponent from "../../../shared/components/PortalPureComponent";

export interface PageLayerProperties extends TextSearchGlobals, DebugGlobals {
	zoomValue: number;
	pageNumber: number;
	attachmentKey?: string;
	context: PortalContext;
	viewerMode: ViewerMode;
	portalDocument: Immutable<PortalDocumentInterface>;
}

export interface PageLayerStatus {
	rawData?: any;
	pageWidth: number;
	pageHeight: number;
}

export class PageLayerDefinition extends PortalPureComponent<PageLayerProperties, PageLayerStatus> {
	/**
	 * page layer constructor
	 */
	constructor(props: Readonly<PageLayerProperties>) {
		super(props);

		this.state = {
			pageHeight: 0,
			pageWidth: 0
		};
	}

	/**
	 * This is called when the component is mounted
	 */
	public async componentDidMount(): Promise<void> {
		await super.componentDidMount();
		await this.getViewerData();
	}

	/**
	 * This is called when the components props or state is updated
	 */
	public async componentDidUpdate(
		prevProps: Readonly<PageLayerProperties>, prevState: Readonly<PageLayerStatus>
	): Promise<void> {
		let documentChanged: Array<boolean> = [
			this.props.portalDocument.get().id !== prevProps.portalDocument.get().id,
			this.props.portalDocument.get().updated !== prevProps.portalDocument.get().updated,
			this.props.zoomValue !== prevProps.zoomValue, this.props.portalResolution !== prevProps.portalResolution,
			this.props.pageNumber !== prevProps.pageNumber,
		];

		if (documentChanged.includes(true)) {
			await this.getViewerData();
		}
	}

	private async getViewerData(): Promise<void> {
		let portalDocument: PortalDocument = this.props.context.documentManager.findDocument(
			this.props.portalDocument.get().id!
		);

		if (!portalDocument.container.loaded) {
			return;
		}

		let rawData: any;
		let viewerData: pdfjs.PDFDocumentProxy;
		if (this.props.viewerMode === ViewerMode.ATTACHMENT ||
			this.props.viewerMode === ViewerMode.PORTFOLIO ||
			this.props.viewerMode === ViewerMode.PORTFOLIO_PREVIEW ||
			this.props.viewerMode === ViewerMode.PORTFOLIO_PAGE) {
			let attachment: Attachment = portalDocument.getAttachment(this.props.attachmentKey!)!;
			viewerData = await portalDocument.loadViewerData(attachment);
			rawData = await portalDocument.getAttachmentData(attachment).getData();
		} else {
			viewerData = await portalDocument.loadViewerData();
			rawData = await portalDocument.data.getData();
		}

		if (!this.mounted) {
			return;
		}

		let pageProxy: pdfjs.PDFPageProxy = await viewerData.getPage(this.props.pageNumber);
		let scaledViewport: pdfjs.PageViewport = pageProxy.getViewport({
			scale: this.props.zoomValue / 100
		});

		this.setState({
			rawData: rawData,
			pageWidth: UnitHelper.unitToPx(scaledViewport.width, LengthUnit.POINTS, this.props.portalResolution),
			pageHeight: UnitHelper.unitToPx(scaledViewport.height, LengthUnit.POINTS, this.props.portalResolution)
		});
	}

	/**
	 * render component as react node
	 */
	public render(): ReactNode {
		return (
			<>
				{FunctionHelper.exists(this.state.rawData) ?
					<Document file={{data: this.state.rawData}} externalLinkTarget={"_blank"} error="" noData=""
							  onPassword={() => {
								  // prevent password confirm popup
							  }}
							  options={{
								  verbosity:
									  portalConfiguration.debug ?
										  pdfjs.VerbosityLevel.INFOS : pdfjs.VerbosityLevel.ERRORS,
								  cMapUrl: 'cmap/',
								  cMapPacked: true
							  }}
							  loading={
								  <div className="loader active">
									  <span className="icon icon-spinner spin"/>
								  </div>
							  }>
						<Page pageNumber={this.props.pageNumber} renderAnnotationLayer={false} renderTextLayer={false}
							  key={this.props.pageNumber} noData="" error=""
							  scale={
								  // pdfjs is using points so scale must be adapted to this
								  UnitHelper.unitToPx(
									  this.props.zoomValue / 100, LengthUnit.POINTS, this.props.portalResolution
								  )
							  }
							  loading={
								  <div className="loader active">
									  <span className="icon icon-spinner spin"/>
								  </div>
							  }/>
					</Document> :
					<div className="loader active">
						<span className="icon icon-spinner spin"/>
					</div>
				}
			</>
		);
	}
}

const PageLayer = React.memo(PageLayerDefinition,
	(prevProps: Readonly<PageLayerProperties>, nextProps: Readonly<PageLayerProperties>) => {
		if (!nextProps.portalDocument.equals(prevProps.portalDocument) &&
			(
				nextProps.portalDocument.get().id !== prevProps.portalDocument?.get().id ||
				nextProps.portalDocument.get().updated !== prevProps.portalDocument?.get().updated
			)
		) {
			return false;
		}

		if (nextProps.attachmentKey !== prevProps.attachmentKey) {
			return false;
		}

		if (nextProps.viewerMode !== prevProps.viewerMode) {
			return false;
		}

		if (nextProps.zoomValue !== prevProps.zoomValue) {
			return false;
		}

		if (nextProps.pageNumber !== prevProps.pageNumber) {
			return false;
		}

		if (nextProps.portalResolution !== prevProps.portalResolution) {
			return false;
		}

		return true;
	}
);

export default PageLayer;